import React, { useEffect } from 'react'

const PrivacyPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
        
        <div className="prose prose-lg max-w-none">
          <p className="text-sm text-gray-600 mb-8">Effective Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. INTRODUCTION</h2>
            <p className="mb-4">
              StockSines ("we," "us," or "our") respects your privacy and is committed to protecting your personal data. This privacy policy will inform you about how we look after your personal data when you visit our website (regardless of where you visit it from) and tell you about your privacy rights and how the law protects you.
            </p>
            
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-6">
              <h3 className="text-lg font-bold text-blue-900 mb-2">IMPORTANT NOTICE</h3>
              <p className="text-blue-800">
                StockSines provides financial market data visualization and analysis tools for educational purposes only. We do not provide personalized financial advice, and we do not access, store, or process any of your personal financial accounts, trading history, or investment portfolio information.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. INFORMATION WE COLLECT</h2>
            
            <h3 className="text-xl font-semibold mb-3">2.1 Information You Provide</h3>
            <p className="mb-4">We collect the following information that you voluntarily provide:</p>
            <ul className="list-disc ml-6 mb-4">
              <li><strong>Email Address:</strong> When you sign up for our waitlist or premium features</li>
              <li><strong>Contact Information:</strong> When you contact us for support</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">2.2 Information Automatically Collected</h3>
            <p className="mb-4">When you visit our Site, we automatically collect certain information:</p>
            <ul className="list-disc ml-6 mb-4">
              <li><strong>Log Data:</strong> IP address, browser type, operating system, referring URLs, pages viewed, and access times</li>
              <li><strong>Usage Data:</strong> Information about how you interact with our visualizations and tools</li>
              <li><strong>Device Information:</strong> Device type, screen resolution, and other technical information</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">2.3 Information We Do NOT Collect</h3>
            <p className="mb-4">We want to be clear about what we do NOT collect:</p>
            <ul className="list-disc ml-6 mb-4">
              <li>Personal financial information (bank accounts, credit cards, etc.)</li>
              <li>Trading account credentials or history</li>
              <li>Investment portfolio details</li>
              <li>Social Security numbers or tax identification numbers</li>
              <li>Personal identification documents</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. HOW WE USE YOUR INFORMATION</h2>
            <p className="mb-4">We use the information we collect for the following purposes:</p>
            <ul className="list-disc ml-6 mb-4">
              <li>To provide and maintain our Service</li>
              <li>To notify you about changes to our Service</li>
              <li>To provide customer support</li>
              <li>To gather analysis or valuable information to improve our Service</li>
              <li>To monitor the usage of our Service</li>
              <li>To detect, prevent, and address technical issues</li>
              <li>To send you updates about new features (if you've opted in)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. DATA STORAGE AND SECURITY</h2>
            
            <h3 className="text-xl font-semibold mb-3">4.1 Where We Store Data</h3>
            <p className="mb-4">
              Email addresses collected through our waitlist are securely stored in our GitHub repository with restricted access. We use industry-standard security measures to protect your information.
            </p>

            <h3 className="text-xl font-semibold mb-3">4.2 Security Measures</h3>
            <p className="mb-4">We implement appropriate technical and organizational measures to protect your personal data:</p>
            <ul className="list-disc ml-6 mb-4">
              <li>Encryption of data in transit (HTTPS)</li>
              <li>Access controls and authentication</li>
              <li>Regular security assessments</li>
              <li>Limited access to personal data on a need-to-know basis</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">4.3 Data Retention</h3>
            <p className="mb-4">
              We retain your personal data only for as long as necessary to fulfill the purposes outlined in this privacy policy. Email addresses on our waitlist are retained until you request removal or unsubscribe.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. COOKIES AND TRACKING</h2>
            
            <h3 className="text-xl font-semibold mb-3">5.1 Cookies We Use</h3>
            <p className="mb-4">We use minimal cookies necessary for site functionality:</p>
            <ul className="list-disc ml-6 mb-4">
              <li><strong>Essential Cookies:</strong> Required for the website to function properly</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website (if implemented)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">5.2 Your Cookie Choices</h3>
            <p className="mb-4">
              You can control and/or delete cookies as you wish. You can delete all cookies that are already on your computer and you can set most browsers to prevent them from being placed.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. THIRD-PARTY SERVICES</h2>
            
            <h3 className="text-xl font-semibold mb-3">6.1 Service Providers</h3>
            <p className="mb-4">We may employ third-party companies and individuals to facilitate our Service:</p>
            <ul className="list-disc ml-6 mb-4">
              <li>Vercel (website hosting)</li>
              <li>GitHub (code repository and data storage)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">6.2 Financial Data Providers</h3>
            <p className="mb-4">
              The options market data displayed on our site is aggregated from public sources. We do not have partnerships that would give us access to your personal trading information from any brokerage or financial institution.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. YOUR RIGHTS</h2>
            <p className="mb-4">You have the following rights regarding your personal data:</p>
            <ul className="list-disc ml-6 mb-4">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate personal data</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data</li>
              <li><strong>Objection:</strong> Object to our processing of your personal data</li>
              <li><strong>Portability:</strong> Request transfer of your personal data to another service</li>
              <li><strong>Withdrawal of Consent:</strong> Withdraw consent for processing where applicable</li>
            </ul>

            <p className="mb-4">To exercise any of these rights, please contact us at support@stocksines.com.</p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. CHILDREN'S PRIVACY</h2>
            <p className="mb-4">
              Our Service is not intended for use by children under the age of 18. We do not knowingly collect personal information from children under 18. If you become aware that a child has provided us with personal data, please contact us.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. CALIFORNIA PRIVACY RIGHTS</h2>
            <p className="mb-4">
              If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA) regarding your personal information. These include the right to know, delete, opt-out, and non-discrimination.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. INTERNATIONAL DATA TRANSFERS</h2>
            <p className="mb-4">
              Your information may be transferred to and maintained on computers located outside of your state, province, country, or other governmental jurisdiction where data protection laws may differ. Your consent to this Privacy Policy followed by your submission of such information represents your agreement to that transfer.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. DO NOT SELL MY INFORMATION</h2>
            <p className="mb-4">
              We do not sell, trade, or otherwise transfer your personal information to third parties. This does not include trusted third parties who assist us in operating our website, conducting our business, or servicing you, so long as those parties agree to keep this information confidential.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. CHANGES TO THIS PRIVACY POLICY</h2>
            <p className="mb-4">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Effective Date" at the top of this Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. CONTACT US</h2>
            <p className="mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <p className="mb-4">
              Email: support@stocksines.com<br/>
              Website: https://stocksines.com
            </p>
          </section>

          <div className="mt-12 p-6 bg-gray-100 rounded-lg">
            <p className="text-sm text-gray-600 text-center">
              By using StockSines, you acknowledge that you have read and understood this Privacy Policy and agree to its terms.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPage